class AltoCollectionPagination extends HTMLElement {
  connectedCallback() {
    // Two buttons render for responsive variants (filled on mobile, text on lg+),
    // both tagged [data-load-more] and only one visible at a time.
    this.buttons = Array.from(this.querySelectorAll('[data-load-more]'));
    this.counter = this.querySelector('[data-pagination-counter]');
    this.counterTemplate = this.counter?.dataset.counterTemplate || null;
    this.progressFill = this.querySelector('.alto-progress-bar__fill');
    this.progressTrack = this.querySelector('.alto-progress-bar');

    this.sectionId = this.dataset.sectionId;
    this.gridId = this.dataset.gridId || 'product-grid';
    this.total = parseInt(this.dataset.total, 10) || 0;
    this.perPage = parseInt(this.dataset.perPage, 10) || 0;
    // Server renders the current page slice; page 1 is already in the DOM.
    this.pagesLoaded = 1;
    this.loading = false;

    if (!this.buttons.length) return;

    this.handleClick = this.handleClick.bind(this);
    this.buttons.forEach((button) => button.addEventListener('click', this.handleClick));
  }

  disconnectedCallback() {
    this.buttons?.forEach((button) => button.removeEventListener('click', this.handleClick));
  }

  /** Current "next page" URL, or null when the last page is loaded. */
  get nextUrl() {
    return this.buttons[0]?.getAttribute('href') || null;
  }

  async handleClick(event) {
    event.preventDefault();
    if (this.loading || !this.buttons.length) return;

    const url = event.currentTarget.getAttribute('href');
    if (!url) return;

    this.setLoading(true);
    try {
      const result = await this.fetchPage(url);
      if (result.cards.length) {
        this.appendCards(result.cards);
        this.pagesLoaded += 1;
      }
      this.setNextUrl(result.nextUrl);
      this.updateCounter(this.gridChildCount());
      this.dispatchEvent(
        new CustomEvent('alto:page-appended', { bubbles: true, detail: { pagesLoaded: this.pagesLoaded } })
      );
    } catch (err) {
      console.error('Load more failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Fetch a page via the Section Rendering API and parse it — no DOM mutation.
   * @returns {Promise<{cards: Element[], nextUrl: string|null}>}
   */
  async fetchPage(url) {
    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.sectionId);

    const response = await fetch(sectionUrl.toString());
    if (!response.ok) throw new Error(`Failed: ${response.status}`);
    const html = await response.text();

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cards = Array.from(doc.querySelectorAll(`#${this.gridId} > li`));
    const nextButton = doc.querySelector('alto-collection-pagination [data-load-more]');
    const nextUrl = nextButton?.getAttribute('href') || null;
    return { cards, nextUrl };
  }

  appendCards(cards) {
    const grid = document.getElementById(this.gridId);
    if (!grid || !cards.length) return;
    const fragment = document.createDocumentFragment();
    cards.forEach((card) => fragment.appendChild(card.cloneNode(true)));
    grid.appendChild(fragment);
  }

  /** Point the load-more button at the next page, or remove it when done. */
  setNextUrl(nextUrl) {
    if (nextUrl) {
      this.buttons.forEach((button) => button.setAttribute('href', nextUrl));
    } else {
      this.buttons.forEach((button) => button.remove());
      this.buttons = [];
    }
  }

  gridChildCount() {
    return document.getElementById(this.gridId)?.children.length || 0;
  }

  /**
   * Rebuild pages 2…targetPage after a browser-back restore. Fetches with a
   * bounded concurrency, then appends in page order. Stops at the first page
   * that fails or returns no cards, keeping the loaded set contiguous.
   * @param {number} targetPage
   * @param {{concurrency?: number}} [options]
   */
  async restoreToPage(targetPage, { concurrency = 4 } = {}) {
    if (!Number.isFinite(targetPage) || targetPage <= this.pagesLoaded) return;

    const pages = [];
    for (let page = this.pagesLoaded + 1; page <= targetPage; page += 1) pages.push(page);

    // Fetch in bounded-concurrency batches; keep results keyed by page number.
    const results = new Map();
    for (let i = 0; i < pages.length; i += concurrency) {
      const batch = pages.slice(i, i + concurrency);
      await Promise.all(
        batch.map(async (page) => {
          try {
            results.set(page, await this.fetchPage(this.pageUrl(page)));
          } catch (err) {
            console.error(`Restore: page ${page} failed:`, err);
            results.set(page, null);
          }
        })
      );
    }

    // Append contiguously in order; a gap (failure or empty) ends the restore.
    let lastNextUrl = this.nextUrl;
    for (const page of pages) {
      const result = results.get(page);
      if (!result || !result.cards.length) break;
      this.appendCards(result.cards);
      this.pagesLoaded = page;
      lastNextUrl = result.nextUrl;
    }

    this.setNextUrl(lastNextUrl);
    this.updateCounter(this.gridChildCount());
  }

  /** Build the collection URL for a given page, preserving active filters. */
  pageUrl(page) {
    const url = new URL(window.location.pathname + window.location.search, window.location.origin);
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  setLoading(isLoading) {
    this.loading = isLoading;
    if (!this.buttons.length) return;
    this.buttons.forEach((button) => {
      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  }

  updateCounter(loadedCount) {
    const safe = Math.min(loadedCount, this.total);
    const progress = this.total > 0 ? (safe / this.total) * 100 : 0;
    if (this.progressFill) this.progressFill.style.width = `${progress}%`;
    if (this.progressTrack) this.progressTrack.setAttribute('aria-valuenow', String(Math.round(progress)));
    if (this.counter) {
      this.counter.textContent = this.counterTemplate
        ? this.counterTemplate.replace('{n}', safe)
        : `${safe} / ${this.total}`;
    }
  }
}

if (!customElements.get('alto-collection-pagination')) {
  customElements.define('alto-collection-pagination', AltoCollectionPagination);
}
